import { Bell, Building2, Check, CheckCheck, ChevronRight, UserPlus, ListChecks, GitBranch, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useWorkspace } from "../context/WorkspaceContext";
import { workspaceService } from "../services/workspaceService";

const POLL_MS = 15000;

function iconFor(type) {
  if (type === "task_assignment") return ListChecks;
  if (type === "diagram_invite") return GitBranch;
  if (type === "workspace_invite") return Building2;
  return UserPlus;
}

function relativeTime(value) {
  const date = new Date(value);
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Ahora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Hace ${days} d`;
  return date.toLocaleDateString("es-PE", { day: "2-digit", month: "short" });
}

export default function NotificationCenter({ collapsed = false }) {
  const navigate = useNavigate();
  const { refresh, setActiveWorkspaceId } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get("/notifications", { params: { limit: 50 } });
      setItems(response.data.items || []);
      setUnread(response.data.unread_count || 0);
    } catch (error) {
      if (!quiet) console.warn("No se pudieron cargar las notificaciones", error);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({ quiet: true });
    const timer = window.setInterval(() => void load({ quiet: true }), POLL_MS);
    const onFocus = () => void load({ quiet: true });
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  useEffect(() => {
    function closeOnOutside(event) {
      if (open && rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    }
    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const grouped = useMemo(() => items, [items]);

  const actionLabel = (status) => ({ accepted: "Aceptaste la invitación", declined: "Rechazaste la invitación", expired: "Invitación expirada", revoked: "Invitación cancelada" }[status]);

  async function markRead(item) {
    if (!item.is_read) {
      setItems((current) => current.map((row) => row.id === item.id ? { ...row, is_read: true } : row));
      setUnread((value) => Math.max(0, value - 1));
      try { await api.patch(`/notifications/${item.id}/read`); } catch { void load({ quiet: true }); }
    }
    setOpen(false);
    if (item.resource_type === "project" && item.resource_ref) navigate(`/gantt/${item.resource_ref}`);
    if (item.resource_type === "diagram" && item.resource_ref) navigate(`/diagramas/${item.resource_ref}`);
  }

  async function markAll() {
    if (!unread) return;
    setItems((current) => current.map((item) => ({ ...item, is_read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch { void load({ quiet: true }); }
  }

  async function respond(item, decision, event) {
    event.stopPropagation();
    try {
      const result = await workspaceService.respondInvitation(item.resource_ref, decision);
      setItems((current) => current.map((row) => row.id === item.id ? { ...row, is_read: true, action_status: decision === "accept" ? "accepted" : "declined", action_responded_at: new Date().toISOString() } : row));
      if (!item.is_read) setUnread((value) => Math.max(0, value - 1));
      if (decision === "accept") {
        await refresh();
        setActiveWorkspaceId(result.workspace_id);
        setOpen(false);
        navigate("/");
      }
    } catch { void load({ quiet: true }); }
  }

  return (
    <div className="notification-center" ref={rootRef}>
      <button
        type="button"
        className={`side-notification-button ${open ? "active" : ""}`}
        onClick={() => { setOpen((value) => !value); void load(); }}
        data-tooltip={collapsed ? "Notificaciones" : undefined}
        aria-label={`Notificaciones${unread ? `, ${unread} sin leer` : ""}`}
        aria-expanded={open}
      >
        <span className="notification-bell-wrap"><Bell size={17} />{unread > 0 && <b>{unread > 99 ? "99+" : unread}</b>}</span>
        {!collapsed && <span>Notificaciones</span>}
      </button>

      {open && (
        <aside className="notification-panel" aria-label="Centro de notificaciones">
          <header>
            <div><h3>Notificaciones</h3><p>{unread ? `${unread} sin leer` : "Todo revisado"}</p></div>
            <button type="button" onClick={markAll} disabled={!unread} title="Marcar todas como leídas"><CheckCheck size={17} /></button>
          </header>
          <div className="notification-list">
            {loading && !items.length && <div className="notification-state">Cargando…</div>}
            {!loading && !items.length && <div className="notification-state">No tienes notificaciones.</div>}
            {grouped.map((item) => {
              const Icon = iconFor(item.notification_type);
              return (
                <div key={item.id} role="button" tabIndex="0" className={`notification-item ${item.is_read ? "" : "unread"}`} onClick={() => markRead(item)} onKeyDown={(event) => { if (event.key === "Enter") markRead(item); }}>
                  <span className="notification-item-icon"><Icon size={16} /></span>
                  <span className="notification-item-copy">
                    <strong>{item.title}</strong>
                    <span>{item.message}</span>
                    <small>{relativeTime(item.created_at)}</small>
                    {item.notification_type === "workspace_invite" && item.action_status === "pending" && <span className="notification-actions"><button type="button" onClick={(event) => respond(item, "decline", event)}><X size={13}/> Rechazar</button><button type="button" className="primary" onClick={(event) => respond(item, "accept", event)}><Check size={13}/> Aceptar</button></span>}
                    {item.notification_type === "workspace_invite" && item.action_status !== "pending" && item.action_status && <span className={`notification-action-state ${item.action_status}`}>{actionLabel(item.action_status) || item.action_status}</span>}
                  </span>
                  <ChevronRight size={15} />
                </div>
              );
            })}
          </div>
        </aside>
      )}
    </div>
  );
}
